import { Suspense, lazy } from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ROUTES } from '@/constants/routes';
import { SkipLink } from '@/components/accessibility/SkipLink';

// Import Landing directly to avoid lazy loading issues on home page
import Landing from '@/pages/Landing';

// Lazy load other pages for code splitting
const Pricing = lazy(() => import('@/pages/Pricing'));
const Login = lazy(() => import('@/pages/Login'));
const Signup = lazy(() => import('@/pages/Signup'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Children = lazy(() => import('@/pages/Children'));
const ChildProfile = lazy(() => import('@/pages/ChildProfile'));
const AddChild = lazy(() => import('@/pages/AddChild'));
const NewAssessment = lazy(() => import('@/pages/NewAssessment'));
const AssessmentScoring = lazy(() => import('@/pages/AssessmentScoring'));
const AssessmentSummary = lazy(() => import('@/pages/AssessmentSummary'));
const Assessments = lazy(() => import('@/pages/Assessments'));
const Reports = lazy(() => import('@/pages/Reports'));
const Settings = lazy(() => import('@/pages/Settings'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" role="status" aria-label="Loading page" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <ErrorBoundary>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <SkipLink />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public Routes */}
                <Route path={ROUTES.HOME} element={<Landing />} />
                <Route path={ROUTES.PRICING} element={<Pricing />} />
                <Route path={ROUTES.LOGIN} element={<Login />} />
                <Route path={ROUTES.SIGNUP} element={<Signup />} />
                <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
                <Route path={ROUTES.RESET_PASSWORD} element={<ResetPassword />} />
                <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermsOfService />} />
                <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />

                {/* Protected Routes */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
                  <Route path={ROUTES.CHILDREN} element={<Children />} />
                  <Route path={ROUTES.CHILD_NEW} element={<AddChild />} />
                  <Route path={ROUTES.CHILD_DETAIL} element={<ChildProfile />} />
                  <Route path={ROUTES.ASSESSMENTS} element={<Assessments />} />
                  <Route path={ROUTES.ASSESSMENTS_NEW} element={<NewAssessment />} />
                  <Route path={ROUTES.ASSESSMENT_SCORING} element={<AssessmentScoring />} />
                  <Route path={ROUTES.ASSESSMENT_SUMMARY} element={<AssessmentSummary />} />
                  <Route path={ROUTES.REPORTS} element={<Reports />} />
                  <Route path={ROUTES.SETTINGS} element={<Settings />} />
                </Route>


                {/* 404 Fallback */}
                <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ErrorBoundary>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;